import { useCallback, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@hooks/redux-store";

import { useAuth0V2 } from "@stadium/services/auth0";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import useEmptyCart from "@hooks/cart/use-empty-cart";
import useCountryChange from "@hooks/use-country-change";
import { useLanguageChange } from "@hooks/use-language-change";
import { useStoreContext } from "@components/store/provider";

import { useConfig } from "@stadium/hooks/lib/use-config";

import { NewCountrySelector } from "@stadium/ui/src/components/country-selector";

import { showSuccessToast } from "@stadium/services/react-toastify";

import useAllCountries from "@hooks/countries/use-all-countries";

import { getCountry } from "@selectors/country";
import { getCartOrderId } from "@selectors/cart";
import { getIsHideCountryFlag } from "@selectors/store";
import { getCurrencies } from "@stadium/redux-utils/selectors/configSelector";

import { updateOrderDetails } from "@actions/order";
import { WeglotContext } from "@stadium/services/weglot/provider";

import { SM_PROGRAM_ID } from "@constants/app-defaults";

import { CART_PAGE_PATHNAME, CART_PAGE_PATHNAME_WITHOUT_WORKSPACE } from "@constants/routes";

import { EMPTY_CART_SUCCESS_TOAST_ID } from "@constants/toast-ids";
import { emptyCart as emptyOrderCart } from "@slices/cart";

import styles from "./main.module.scss";

export const CountrySelector = ({ onSelect = null }) => {
  const router = useRouter();
  const { pathname: routerPathName } = router;

  const dispatch = useAppDispatch();

  const { isAuthenticated } = useAuth0V2();

  const selectedCountry = useSelector(getCountry);
  const orderId = useSelector(getCartOrderId);
  const hideCountryFlag = useSelector(getIsHideCountryFlag);
  const currencies = useConfig(getCurrencies);

  const { getUpdatedCatalogBasePath, displayCurrency, setDisplayCurrency } = useStoreContext();

  const { instance: Weglot, availableLanguages } = useContext(WeglotContext);
  const { emptyCart } = useEmptyCart();

  const { allCountriesList } = useAllCountries({
    includeGlobalCountry: false,
  });

  const { changeCountry } = useCountryChange(allCountriesList);
  const { changeLang, langInUrl } = useLanguageChange();

  const isCartPage = useMemo(
    () => [CART_PAGE_PATHNAME, CART_PAGE_PATHNAME_WITHOUT_WORKSPACE].includes(routerPathName),
    [routerPathName],
  );

  const redirectToMenuRoute = useCallback(
    (country) => {
      router.replace(
        getUpdatedCatalogBasePath({
          countryCode: country.iso.toLowerCase(),
        }),
      );
    },
    [router, getUpdatedCatalogBasePath],
  );

  const updateOrderCountry = useCallback(
    async (country) => {
      if (!isAuthenticated) {
        dispatch(emptyOrderCart());
        showSuccessToast({
          message: "All the items have been removed from your bag.",
          config: { toastId: EMPTY_CART_SUCCESS_TOAST_ID },
        });
        return;
      }
      await dispatch(
        updateOrderDetails(orderId, { program_id: SM_PROGRAM_ID, country_id: country.id }),
      );
      await emptyCart(orderId);
    },
    [dispatch, emptyCart, isAuthenticated, orderId],
  );

  const onSelectCountry = useCallback(
    async (country) => {
      if (orderId) {
        await updateOrderCountry(country);
      }
      if (isCartPage) {
        return redirectToMenuRoute(country);
      }
      changeCountry(country);
    },
    [changeCountry, isCartPage, orderId, redirectToMenuRoute, updateOrderCountry],
  );

  const currencySelectorProps = useMemo(() => {
    return {
      setCurrency: setDisplayCurrency,
      currency: displayCurrency,
    };
  }, [setDisplayCurrency, displayCurrency]);

  return (
    <div className={styles.dropdownBlock}>
      <NewCountrySelector
        currencies={currencies}
        currencySelectorProps={currencySelectorProps}
        countries={allCountriesList}
        onSelectCountry={onSelect ?? onSelectCountry}
        selectedCountry={selectedCountry}
        flagWidth={32}
        flagHeight={24}
        weglotInstance={Weglot}
        availableLanguages={availableLanguages}
        onSelectLang={changeLang}
        langInUrl={langInUrl}
        type="round"
        showCountryChangeModal
        arrowCustomClass={styles.dropdownArrowIcon}
        hideCountryFlag={hideCountryFlag}
      />
    </div>
  );
};

export default CountrySelector;
